import { useId } from 'react';

/**
 * The Raad brand mark — a signal pulse rising over a route pin, in the green
 * business gradient (#12B76A → #3CE596). The ONLY colour literals deliberately
 * left outside the palette: a brand mark is the same green on any ground, so a
 * theme must NOT re-point it. This is the mark served by
 * raadtelematics.com (`/assets/logo.svg`) and the app favicon (`public/
 * favicon.svg`); rendering it here keeps the login screen, the app shell and the
 * marketing site the same brand. Green only — the indigo admin mark lives in
 * admin/.
 *
 * Inline SVG on purpose: it paints with the JS bundle that is already loading,
 * with no extra HTTP request — one fewer round trip on a Mogadishu 3G link than
 * an <img src> would cost. The served file asset (for the browser tab and link
 * previews) is `public/favicon.svg` / `public/logo.svg`; this component is the
 * in-app render of the same mark.
 *
 * NOTE: the source SVG is self-labelled a PLACEHOLDER on the marketing site
 * ("the real logo must be finalized before Phase 2 onboarding"). Adopting it
 * makes the app consistent with the site but does NOT finalise the brand — that
 * decision is Mahad's. `size` drives the square; the artwork scales with it.
 */
export function RaadLogo({ size = 40, title }: { size?: number; title?: string }) {
  // A stable-per-instance gradient id so multiple marks on one page don't clash.
  // Keyed on useId(), NOT on size: two same-size marks (e.g. the shell's 30 and
  // an error screen's) would otherwise emit duplicate DOM ids for their <linearGradient>.
  const gid = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#12B76A" />
          <stop offset="1" stopColor="#3CE596" />
        </linearGradient>
      </defs>
      <g fill="none" stroke={`url(#${gid})`} strokeWidth="2.6" strokeLinecap="round">
        <path d="M6 27a14 14 0 0 1 28 0" />
        <path d="M12 27a8 8 0 0 1 16 0" />
      </g>
      <circle cx="20" cy="27" r="3.6" fill={`url(#${gid})`} />
    </svg>
  );
}
